// src/ui.js
// Toda a lógica de renderização do DOM
// Não faz chamadas de API nem acessa Firestore — só desenha

import { PHILOSOPHERS, TEACHER } from './data/philosophers.js'

// ── ÍCONES SVG (outline, sem cor) ──────────────────────────────────────────
export const ICONS = {
  search:   `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  newGroup: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>`,
  more:     `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="19" r="1" fill="currentColor"/></svg>`,
  send:     `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`,
  columns:  `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.3"><rect x="2" y="20" width="20" height="2" rx="1"/><rect x="6" y="10" width="2" height="10"/><rect x="11" y="10" width="2" height="10"/><rect x="16" y="10" width="2" height="10"/><path d="M2 7l10-5 10 5"/></svg>`,
}

// ── AVATAR HELPER ──────────────────────────────────────────────────────────
// Retorna HTML de <img> com fallback para emoji se photoURL falhar ou não existir
function _avatarImg(photoURL, emoji, cls = '') {
  if (!photoURL) return _escapeHtml(emoji || '')
  const safeEmoji = _escapeHtml(emoji || '?').replace(/'/g, "\\'")
  const safeSrc = _escapeHtml(photoURL)
  return `<img src="${safeSrc}" alt="" class="av-img ${cls}" onerror="this.replaceWith(document.createTextNode('${safeEmoji}'))">`
}

// ── SANITIZAÇÃO HTML ───────────────────────────────────────────────────────
// Escapa caracteres perigosos ANTES de aplicar formatação — previne XSS
export function _escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

// ── FORMATAÇÃO DE TEXTO (estilo WhatsApp) ──────────────────────────────────
function _formatText(text) {
  return _escapeHtml(text)
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')         // **negrito** (Markdown)
    .replace(/\*(.+?)\*/g, '<b>$1</b>')              // *negrito* (WhatsApp)
    .replace(/_(.+?)_/g, '<i>$1</i>')                // _itálico_
    .replace(/~(.+?)~/g, '<del>$1</del>')            // ~riscado~
    .replace(/```(.+?)```/gs, '<code>$1</code>')     // ```código```
    .replace(/`(.+?)`/g, '<code>$1</code>')          // `código inline`
}

// ── TEMPO ───────────────────────────────────────────────────────────────────
export function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

// ── SIDEBAR ─────────────────────────────────────────────────────────────────
export function renderChatList({ chats, activeId, filter, query, onOpen }) {
  const list = document.getElementById('chat-list')
  if (!list) return

  const q = (query || '').toLowerCase()

  let items = chats.filter((c) => {
    if (filter === 'groups') return c.type === 'group'
    if (filter === 'dms')    return c.type === 'dm'
    if (filter === 'unread') return c.unread > 0
    return true
  }).filter((c) => c.name.toLowerCase().includes(q))

  // Ordena por última mensagem
  items.sort((a, b) => {
    const at = a.messages?.slice(-1)[0]?.ts || 0
    const bt = b.messages?.slice(-1)[0]?.ts || 0
    return bt - at
  })

  if (items.length === 0) {
    list.innerHTML = `<div class="list-empty">Nenhuma conversa${filter !== 'all' ? ' nesta categoria' : ''}.</div>`
    return
  }

  list.innerHTML = ''
  items.forEach((c) => {
    const last = c.messages?.slice(-1)[0]
    const isUnread = c.unread > 0
    const isActive = c.id === activeId

    const el = document.createElement('div')
    el.className = `chat-item${isActive ? ' active' : ''}${isUnread ? ' has-unread' : ''}`
    el.onclick = () => onOpen(c)

    // Avatar
    let avHtml = ''
    if (c.type === 'group') {
      const ids = (c.philIds || []).slice(0, 4)
      const collageClass = ids.length > 1 ? 'av-collage' : ''
      const imgs = ids.map((id) => {
        const p = PHILOSOPHERS[id]
        return p ? `<span class="av-collage-item">${_avatarImg(p.photoURL, p.emoji)}</span>` : ''
      }).join('')
      avHtml = `<div class="avatar group ${collageClass}">${imgs}</div>`
    } else {
      const p = PHILOSOPHERS[c.philId]
      avHtml = `<div class="avatar dm">${_avatarImg(p?.photoURL, p?.emoji || '?')}</div>`
    }

    // Preview
    let previewHtml = ''
    if (last) {
      const rawText = last.text.replace(/^\w[\w\s]+:\s+/, '')
      const preview = _escapeHtml(rawText.substring(0, 40) + (rawText.length > 40 ? '…' : ''))
      const senderPrefix = last.sender !== 'Você' && c.type === 'group'
        ? `<span class="preview-sender">${_escapeHtml(last.sender.split(' ')[0])}:</span> `
        : ''
      previewHtml = senderPrefix + preview
    } else {
      previewHtml = `<em class="preview-empty">Sem mensagens</em>`
    }

    el.innerHTML = `
      ${avHtml}
      <div class="chat-item-info">
        <div class="chat-item-top">
          <div class="chat-item-name">${_escapeHtml(c.name)}</div>
          <div class="chat-item-time">${last ? fmtTime(last.ts) : _escapeHtml(c.classTime || '')}</div>
        </div>
        <div class="chat-item-preview">${previewHtml}</div>
      </div>
      ${isUnread ? `<div class="unread">${c.unread}</div>` : ''}
    `
    list.appendChild(el)
  })
}

// ── CHAT HEADER ─────────────────────────────────────────────────────────────
export function renderChatHeader(chat) {
  const av   = document.getElementById('ch-av')
  const name = document.getElementById('ch-name')
  const sub  = document.getElementById('ch-sub')
  if (!av || !name || !sub) return

  if (chat.type === 'group') {
    const ids = (chat.philIds || []).slice(0, 4)
    const collageClass = ids.length > 1 ? 'av-collage' : ''
    av.innerHTML = ids.map((id) => {
      const p = PHILOSOPHERS[id]
      return p ? `<span class="av-collage-item">${_avatarImg(p.photoURL, p.emoji)}</span>` : ''
    }).join('')
    av.style.fontSize = '0.85rem'
    av.className = `avatar group ${collageClass}`
    sub.textContent = [TEACHER.name, ...(chat.philIds || []).map((id) => PHILOSOPHERS[id]?.name)].filter(Boolean).sort().join(', ')
  } else {
    const p = PHILOSOPHERS[chat.philId]
    av.innerHTML = _avatarImg(p?.photoURL, p?.emoji || '?')
    av.style.fontSize = '1.5rem'
    av.className = 'avatar dm'
    sub.textContent = p ? `${p.era} · ${p.tags.join(', ')}` : ''
  }
  name.textContent = chat.name  // textContent is safe — no HTML injection

  const input = document.getElementById('msg-input')
  if (input) {
    input.placeholder = chat.type === 'group'
      ? 'Mensagem no grupo…'
      : `Mensagem para ${PHILOSOPHERS[chat.philId]?.name || ''}…`
  }
}

// ── MENSAGENS ────────────────────────────────────────────────────────────────

function _isNearBottom(wrap, threshold = 80) {
  return wrap.scrollHeight - wrap.scrollTop - wrap.clientHeight < threshold
}

let _newMsgCount = 0

export function renderMessages(messages, chatType) {
  const wrap = document.getElementById('messages-wrap')
  if (!wrap) return

  // Preserva typing indicators antes de limpar
  const typingEls = [...wrap.querySelectorAll('.typing-row')]

  wrap.innerHTML = ''
  _newMsgCount = 0
  _updateNewMsgBadge(0)

  // Clear badge when user scrolls to bottom
  wrap.onscroll = () => {
    if (_isNearBottom(wrap) && _newMsgCount > 0) {
      _newMsgCount = 0
      _updateNewMsgBadge(0)
    }
  }

  if (!messages || messages.length === 0) {
    // Re-append typing indicators
    typingEls.forEach((el) => wrap.appendChild(el))
    return
  }

  appendDateSep(wrap, 'Hoje')
  messages.forEach((m) => appendMessage(wrap, m, chatType, true))

  // Re-append typing indicators
  typingEls.forEach((el) => wrap.appendChild(el))

  wrap.scrollTop = wrap.scrollHeight
}

export function appendMessage(wrap, m, chatType, skipScrollCheck = false) {
  if (!wrap) wrap = document.getElementById('messages-wrap')
  if (!wrap) return

  const shouldScroll = skipScrollCheck || _isNearBottom(wrap)

  const isOut    = m.sender === 'Você'
  const philEntry = Object.entries(PHILOSOPHERS).find(([, v]) => v.name === m.sender)
  const isTeacher = m.sender === TEACHER.name

  const block = document.createElement('div')
  block.className = `msg-block ${isOut ? 'out' : 'in'}`

  const row = document.createElement('div')
  row.className = 'msg-row'

  // Avatar (apenas mensagens recebidas)
  if (!isOut) {
    const av = document.createElement('div')
    av.className = 'msg-av'
    if (philEntry) {
      av.innerHTML = _avatarImg(philEntry[1].photoURL, philEntry[1].emoji)
      av.title = 'Ver perfil'
      av.style.cursor = 'pointer'
      av.dataset.philId = philEntry[0]
    } else if (isTeacher) {
      av.innerHTML = _avatarImg(TEACHER.photoURL, TEACHER.emoji)
    } else {
      av.textContent = '👤'
    }
    row.appendChild(av)
  }

  // Bolha
  const bubble = document.createElement('div')
  bubble.className = 'bubble'

  // Nome do remetente (só no grupo, mensagens recebidas)
  if (!isOut && chatType === 'group') {
    const senderEl = document.createElement('div')
    senderEl.className = 'bubble-sender'
    const color = philEntry ? philEntry[1].color : (isTeacher ? TEACHER.color : 'var(--accent)')
    senderEl.style.color = color
    senderEl.textContent = m.sender  // textContent is safe
    bubble.appendChild(senderEl)
  }

  const textEl = document.createElement('div')
  textEl.innerHTML = _formatText(m.text)  // _formatText escapes HTML first
  bubble.appendChild(textEl)

  const timeEl = document.createElement('div')
  timeEl.className = 'bubble-time'
  timeEl.textContent = fmtTime(m.ts)
  bubble.appendChild(timeEl)

  row.appendChild(bubble)
  block.appendChild(row)

  const messagesWrap = document.getElementById('messages-wrap')
  if (messagesWrap) {
    messagesWrap.appendChild(block)
    if (shouldScroll) {
      requestAnimationFrame(() => { if (messagesWrap.scrollTo) messagesWrap.scrollTo({ top: messagesWrap.scrollHeight, behavior: 'smooth' }) })
    } else if (!skipScrollCheck && !isOut) {
      _newMsgCount++
      _updateNewMsgBadge(_newMsgCount)
    }
  }
}

function _updateNewMsgBadge(count) {
  let badge = document.getElementById('new-msg-badge')
  if (count <= 0) {
    if (badge) badge.style.display = 'none'
    return
  }
  if (!badge) {
    badge = document.createElement('button')
    badge.id = 'new-msg-badge'
    badge.className = 'new-msg-badge'
    badge.onclick = () => {
      const wrap = document.getElementById('messages-wrap')
      if (wrap?.scrollTo) wrap.scrollTo({ top: wrap.scrollHeight, behavior: 'smooth' })
      _newMsgCount = 0
      _updateNewMsgBadge(0)
    }
    document.getElementById('messages-wrap')?.parentElement?.appendChild(badge)
  }
  badge.textContent = count
  badge.style.display = ''
}

function appendDateSep(wrap, text) {
  const d = document.createElement('div')
  d.className = 'date-sep'
  d.innerHTML = `<span>${text}</span>`
  wrap.appendChild(d)
}

// ── TYPING INDICATOR ────────────────────────────────────────────────────────
export function setTyping(name, emoji, color, visible) {
  const wrap = document.getElementById('messages-wrap')
  if (!wrap) return
  const id = 'typing_' + name.replace(/\s/g, '_')

  if (visible) {
    if (document.getElementById(id)) return
    const philEntry = Object.entries(PHILOSOPHERS).find(([, v]) => v.name === name)
    const photoURL = philEntry ? philEntry[1].photoURL : (name === TEACHER.name ? TEACHER.photoURL : null)
    const avContent = _avatarImg(photoURL, emoji)
    const el = document.createElement('div')
    el.className = 'typing-row'
    el.id = id
    el.innerHTML = `
      <div class="msg-av">${avContent}</div>
      <div class="typing-bubble">
        <div class="tdot"></div><div class="tdot"></div><div class="tdot"></div>
      </div>
      <span class="typing-name" style="color:${color}">${name} está digitando…</span>
    `
    wrap.appendChild(el)
    requestAnimationFrame(() => { if (wrap.scrollTo) wrap.scrollTo({ top: wrap.scrollHeight, behavior: 'smooth' }) })
  } else {
    document.getElementById(id)?.remove()
  }
}

// ── WAITING STATE (grupo aguardando professora) ─────────────────────────────
export function renderWaitingState() {
  const wrap = document.getElementById('messages-wrap')
  if (!wrap) return
  wrap.innerHTML = `
    <div class="waiting-state">
      <div class="waiting-spinner"></div>
      <div class="waiting-title">Preparando o debate…</div>
    </div>
  `
}

// ── NAVEGACAO EM PILHA DOS PAINEIS ──────────────────────────────────────────
const panelStack = []

const BACK_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`

export function openPanel(type, data) {
  panelStack.length = 0
  panelStack.push({ type, data })
  _renderPanelStack()
}

export function pushPanel(type, data) {
  panelStack.push({ type, data })
  _renderPanelStack()
}

export function popPanel() {
  if (panelStack.length <= 1) { clearPanels(); return }
  panelStack.pop()
  _renderPanelStack()
}

export function clearPanels() {
  panelStack.length = 0
  document.getElementById('group-info-panel')?.classList.remove('open')
  document.getElementById('profile-panel')?.classList.remove('open')
  document.getElementById('admin-panel')?.classList.remove('open')
}

export function getPanelStack() { return panelStack }

function _renderPanelStack() {
  const panel = document.getElementById('group-info-panel')
  if (!panel || panelStack.length === 0) return

  const entry = panelStack[panelStack.length - 1]
  const isRoot = panelStack.length === 1

  const headerHtml = `
    <div class="panel-header">
      ${!isRoot ? `<button class="panel-back-btn" id="panel-back">${BACK_SVG}</button><span class="panel-header-title">Dados do contato</span>` : ''}
      <button class="pp-close" id="gi-close">&#x2715;</button>
    </div>
  `

  let topHtml = ''
  let bodyHtml = ''

  if (entry.type === 'group') {
    const r = _buildGroupContent(entry.data)
    topHtml = r.topHtml; bodyHtml = r.bodyHtml
  } else if (entry.type === 'phil') {
    const r = _buildPhilContent(entry.data)
    topHtml = r.topHtml; bodyHtml = r.bodyHtml
  } else if (entry.type === 'teacher') {
    const r = _buildTeacherContent()
    topHtml = r.topHtml; bodyHtml = r.bodyHtml
  }

  document.getElementById('gi-top').innerHTML = headerHtml + topHtml
  document.getElementById('gi-body').innerHTML = bodyHtml

  document.getElementById('gi-close')?.addEventListener('click', clearPanels, { once: true })
  document.getElementById('panel-back')?.addEventListener('click', popPanel, { once: true })

  if (entry.type === 'group') {
    _wireGroupClicks(entry.data)
  } else if (entry.type === 'phil' && entry.data.onDM) {
    document.getElementById('panel-dm-btn')?.addEventListener('click', () => {
      const { philId, onDM } = entry.data
      clearPanels()
      onDM(philId)
    }, { once: true })
  }

  panel.classList.add('open')
  document.getElementById('profile-panel')?.classList.remove('open')
}

function _buildGroupContent({ chat }) {
  const philIds = chat.philIds || []
  const emojis = philIds.slice(0, 3).map((id) => PHILOSOPHERS[id]?.emoji || '').join('')

  let createdStr = ''
  if (chat.createdAt) {
    const d = chat.createdAt.toDate ? chat.createdAt.toDate() : new Date(chat.createdAt)
    createdStr = d.toLocaleDateString('pt-BR')
  }

  const sortedPhils = [...philIds]
    .map((id) => ({ id, phil: PHILOSOPHERS[id] }))
    .filter((x) => x.phil)
    .sort((a, b) => a.phil.name.localeCompare(b.phil.name))

  const collageClass = philIds.length > 1 ? 'av-collage' : ''
  const collageImgs = philIds.slice(0, 4).map((id) => {
    const p = PHILOSOPHERS[id]
    return p ? `<span class="av-collage-item">${_avatarImg(p.photoURL, p.emoji)}</span>` : ''
  }).join('')

  const topHtml = `
    <div class="pp-avatar ${collageClass}" style="font-size:1.6rem">${collageImgs}</div>
    <div class="pp-name">${chat.name}</div>
    <div class="pp-era">Tema: ${chat.topic || '—'}</div>
    ${chat.classTime ? `<div class="pp-era" style="margin-top:2px">Aula às ${chat.classTime}</div>` : ''}
    ${createdStr ? `<div class="pp-era" style="margin-top:2px">Criado em ${createdStr}</div>` : ''}
  `

  const bodyHtml = `
    <div class="psec">
      <div class="psec-title">Membros</div>
      <div class="gi-phil-list">
        <div class="gi-phil" data-role="teacher">
          <span class="gi-phil-emoji">${_avatarImg(TEACHER.photoURL, TEACHER.emoji)}</span>
          <span class="gi-phil-name">${TEACHER.name}</span>
          <span class="gi-member-badge">Professora</span>
        </div>
        ${sortedPhils.map(({ id, phil }) => `
          <div class="gi-phil" data-phil-id="${id}">
            <span class="gi-phil-emoji">${_avatarImg(phil.photoURL, phil.emoji)}</span>
            <span class="gi-phil-name">${phil.name}</span>
            <span class="gi-phil-tag">${phil.tags[0] || ''}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `

  return { topHtml, bodyHtml }
}

function _wireGroupClicks({ onDM }) {
  const body = document.getElementById('gi-body')
  if (!body) return

  body.querySelector('[data-role="teacher"]')?.addEventListener('click', () => {
    pushPanel('teacher', {})
  })

  body.querySelectorAll('.gi-phil[data-phil-id]').forEach((el) => {
    el.addEventListener('click', () => {
      pushPanel('phil', { philId: el.dataset.philId, onDM })
    })
  })
}

function _buildPhilContent({ philId }) {
  const p = PHILOSOPHERS[philId]
  if (!p) return { topHtml: '', bodyHtml: '' }

  const topHtml = `
    <div class="pp-avatar">${_avatarImg(p.photoURL, p.emoji, 'av-img-lg')}</div>
    <div class="pp-name">${p.name}</div>
    <div class="pp-era">${p.era}</div>
    <div class="pp-tags">${p.tags.map((t) => `<span class="pp-tag">${t}</span>`).join('')}</div>
    <button class="dm-btn" id="panel-dm-btn">💬 Enviar mensagem</button>
  `

  const bodyHtml = `
    <div class="psec"><div class="psec-title">Sobre</div><p>${p.bio}</p></div>
    <div class="psec"><div class="psec-title">Pensamento central</div><p>${p.thought}</p></div>
    <div class="psec"><div class="psec-title">Obras principais</div>${p.works.map((w) => `<div class="pwork">${w}</div>`).join('')}</div>
  `

  return { topHtml, bodyHtml }
}

function _buildTeacherContent() {
  const topHtml = `
    <div class="pp-avatar">${_avatarImg(TEACHER.photoURL, TEACHER.emoji, 'av-img-lg')}</div>
    <div class="pp-name">${TEACHER.name}</div>
    <div class="pp-era">${TEACHER.era}</div>
    <div class="pp-tags">${TEACHER.tags.map((t) => `<span class="pp-tag">${t}</span>`).join('')}</div>
  `

  const bodyHtml = `
    <div class="psec"><div class="psec-title">Sobre</div><p>${TEACHER.bio}</p></div>
    <div class="psec"><div class="psec-title">Pensamento central</div><p>${TEACHER.thought}</p></div>
  `

  return { topHtml, bodyHtml }
}

// Compat wrappers
export function showProfile(philId, onDM) {
  if (!PHILOSOPHERS[philId]) return
  openPanel('phil', { philId, onDM })
}

export function closeProfile() { clearPanels() }

export function showTeacherProfile() { openPanel('teacher', {}) }

export function showGroupInfo(chat, onPhilClick, onTeacherClick) {
  openPanel('group', { chat, onDM: onPhilClick })
}

export function closeGroupInfo() { clearPanels() }

// ── LISTA DE CONTATOS (filósofos) ──────────────────────────────────────────

export function renderContactsList(onOpenDM, onShowProfile) {
  const list = document.getElementById('contacts-list')
  if (!list) return

  const sorted = Object.entries(PHILOSOPHERS)
    .sort(([, a], [, b]) => a.name.localeCompare(b.name))

  list.innerHTML = ''
  sorted.forEach(([id, p]) => {
    const el = document.createElement('div')
    el.className = 'chat-item'

    el.innerHTML = `
      <div class="avatar dm contact-avatar" data-phil-id="${id}" style="cursor:pointer">
        ${_avatarImg(p.photoURL, p.emoji)}
      </div>
      <div class="chat-item-info">
        <div class="chat-item-name">${p.name}</div>
        <div class="chat-item-preview" style="color:var(--text-muted)">${p.tags[0] || ''}</div>
      </div>
    `

    // Click on entire item → open DM
    el.addEventListener('click', () => {
      onOpenDM(id)
    })

    // Click on avatar specifically → profile (stops propagation to prevent DM)
    el.querySelector('.contact-avatar').addEventListener('click', (e) => {
      e.stopPropagation()
      onShowProfile(id)
    })

    list.appendChild(el)
  })
}

// ── ADMIN PANEL ────────────────────────────────────────────────────────────

export function showAdminPanel() {
  clearPanels()
  document.getElementById('admin-panel')?.classList.add('open')
  // Limpa estado anterior
  document.getElementById('admin-search-email').value = ''
  document.getElementById('admin-user-result').style.display = 'none'
}

export function closeAdminPanel() {
  document.getElementById('admin-panel')?.classList.remove('open')
}

export function renderAdminUserResult(user, onChangeRole) {
  const container = document.getElementById('admin-user-result')
  if (!container) return

  const isTeacher = user.role === 'teacher'
  const initial = (user.name || user.email || '?')[0].toUpperCase()
  const avatarHtml = user.photoURL
    ? `<img src="${user.photoURL}" alt="" referrerpolicy="no-referrer" class="admin-user-photo">`
    : `<span class="admin-user-initial">${initial}</span>`

  const badgeClass = isTeacher ? 'admin-badge-teacher' : 'admin-badge-student'
  const badgeText = isTeacher ? 'Professor' : 'Aluno'
  const btnText = isTeacher ? 'Tornar Aluno' : 'Tornar Professor'
  const newRole = isTeacher ? 'student' : 'teacher'

  container.innerHTML = `
    <div class="admin-user-card">
      <div class="admin-user-avatar">${avatarHtml}</div>
      <div class="admin-user-info">
        <div class="admin-user-name">${user.name || '—'}</div>
        <div class="admin-user-email">${user.email}</div>
        <span class="admin-badge ${badgeClass}">${badgeText}</span>
      </div>
    </div>
    <button class="btn-primary" id="admin-change-role-btn" style="width:100%;margin-top:12px">${btnText}</button>
  `
  container.style.display = 'block'

  document.getElementById('admin-change-role-btn')?.addEventListener('click', () => {
    onChangeRole(user.uid, newRole)
  }, { once: true })
}

// ── MODAL CRIAR GRUPO ────────────────────────────────────────────────────────
export function buildPhilGrid(selectedIds = [], onToggle) {
  const grid = document.getElementById('phil-grid')
  if (!grid) return
  grid.innerHTML = ''

  Object.entries(PHILOSOPHERS).forEach(([id, p]) => {
    const el = document.createElement('div')
    const isSel = selectedIds.includes(id)
    el.className = `phil-item${isSel ? ' sel' : ''}`
    el.dataset.id = id
    el.innerHTML = `
      <span>${p.emoji}</span>
      <span style="flex:1">${p.name}</span>
      <span style="font-size:0.7rem;color:var(--text-muted)">${p.tags[0]}</span>
      <span class="pcheck">${isSel ? '✓' : ''}</span>
    `
    el.onclick = () => {
      el.classList.toggle('sel')
      el.querySelector('.pcheck').textContent = el.classList.contains('sel') ? '✓' : ''
      onToggle(id, el.classList.contains('sel'))
    }
    grid.appendChild(el)
  })
}

export function getSelectedPhils() {
  return [...document.querySelectorAll('#phil-grid .phil-item.sel')].map((el) => el.dataset.id)
}

// ── STATUS (stories dos filósofos) ──────────────────────────────────────────

function _timeAgo(ts) {
  if (!ts) return ''
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  const diff = Math.floor((Date.now() - d.getTime()) / 1000)
  if (diff < 60) return 'agora'
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`
  return `há ${Math.floor(diff / 3600)}h`
}

export function renderStatusList(statuses, viewedIds, onOpen) {
  const list = document.getElementById('status-list')
  if (!list) return

  if (!statuses || statuses.length === 0) {
    list.innerHTML = '<div class="list-empty">Nenhum status hoje ainda.</div>'
    return
  }

  // Não vistos primeiro
  const sorted = [...statuses].sort((a, b) => {
    const aViewed = viewedIds.includes(a.id) ? 1 : 0
    const bViewed = viewedIds.includes(b.id) ? 1 : 0
    return aViewed - bViewed
  })

  list.innerHTML = ''
  sorted.forEach((s) => {
    const phil = PHILOSOPHERS[s.philId]
    if (!phil) return
    const viewed = viewedIds.includes(s.id)

    const el = document.createElement('div')
    el.className = 'chat-item'
    el.onclick = () => onOpen(s)

    el.innerHTML = `
      <div class="status-avatar ${viewed ? 'viewed' : ''}">
        ${_avatarImg(phil.photoURL, phil.emoji)}
      </div>
      <div class="chat-item-info">
        <div class="chat-item-top">
          <div class="chat-item-name">${phil.name}</div>
          <div class="chat-item-time">${_timeAgo(s.createdAt)}</div>
        </div>
        <div class="chat-item-preview" style="color:var(--text-muted)">Toque para ver</div>
      </div>
    `
    list.appendChild(el)
  })
}

export function showStatusViewer(status, userId, onViewed) {
  const viewer = document.getElementById('status-viewer')
  if (!viewer) return

  const phil = PHILOSOPHERS[status.philId]
  if (!phil) return

  document.getElementById('sv-avatar').innerHTML = _avatarImg(phil.photoURL, phil.emoji)
  document.getElementById('sv-name').textContent = phil.name
  document.getElementById('sv-time').textContent = _timeAgo(status.createdAt)
  document.getElementById('sv-image').src = status.imageURL
  document.getElementById('sv-caption').textContent = status.caption

  // Reinicia animação da barra de progresso
  const fill = viewer.querySelector('.status-progress-fill')
  fill.style.animation = 'none'
  fill.offsetHeight // force reflow
  fill.style.animation = 'statusTimer 5s linear forwards'

  viewer.style.display = 'flex'

  // Marca como visto
  if (onViewed) onViewed()

  // Auto-fecha após 5s
  const timer = setTimeout(closeStatusViewer, 5000)

  // Close button
  const closeBtn = document.getElementById('sv-close')
  const handler = () => { clearTimeout(timer); closeStatusViewer() }
  closeBtn.onclick = handler

  // Click na imagem também fecha
  document.getElementById('sv-image').onclick = handler
}

export function closeStatusViewer() {
  const viewer = document.getElementById('status-viewer')
  if (viewer) viewer.style.display = 'none'
}

export function updateStatusBadge(count) {
  const badge = document.getElementById('nav-status-badge')
  if (!badge) return
  if (count > 0) {
    badge.textContent = count
    badge.style.display = ''
  } else {
    badge.style.display = 'none'
  }
}

// ── TELA DE LOGIN ────────────────────────────────────────────────────────────
export function renderLoginScreen() {
  // Usa overlay em vez de substituir o body — preserva o DOM do app
  let screen = document.getElementById('login-screen')
  if (!screen) {
    screen = document.createElement('div')
    screen.id = 'login-screen'
    document.body.appendChild(screen)
  }
  screen.style.display = 'flex'
  screen.innerHTML = `
    <div class="login-card">
      <div class="login-logo">Philosapp</div>
      <div class="login-sub">Debate com os grandes filósofos</div>
      <button id="login-btn" class="login-btn">
        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        Entrar com Google
      </button>
      <div class="login-hint">Acesse com sua conta Google institucional ou pessoal</div>
    </div>
  `
}

export function hideLoginScreen() {
  const screen = document.getElementById('login-screen')
  if (screen) screen.style.display = 'none'
}

// ── TOAST ────────────────────────────────────────────────────────────────────
export function showToast(msg, color) {
  const t = document.getElementById('toast')
  if (!t) return
  t.textContent = msg
  t.style.background = color || 'var(--accent)'
  t.classList.add('show')
  setTimeout(() => t.classList.remove('show'), 2800)
}